import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { Wand2, Trophy, Users, Brain, Sparkles, Award, Target, Zap, Star, Info } from 'lucide-react';

export default function HomePage() {
  return (
    <>
      <Helmet>
        <title>Name Generators AI | AI-Powered Name Generators for Teams, Brands & More</title>
      </Helmet>
      <div className="relative">
        <div className="absolute inset-0">
          <img
            className="w-full h-full object-cover"
            src="https://images.unsplash.com/photo-1461896836934-ffe607ba8211?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=2070&q=80"
            alt="Team celebration"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-indigo-900 via-indigo-800 to-indigo-900 opacity-85"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 text-center">
          <div className="flex justify-center mb-6">
            <Wand2 className="w-16 h-16 text-indigo-400 animate-bounce" />
          </div>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
            Team Name Genius
          </h1>
          <p className="text-xl text-indigo-100 mb-8 max-w-3xl mx-auto">
            The Ultimate AI-Powered Team Name Generator for Sports, Fantasy Leagues, Gaming & More
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 max-w-4xl mx-auto mb-8">
            <Link to="/sports" className="bg-white/10 p-4 rounded-lg hover:bg-white/20 transition-colors">
              <Trophy className="w-8 h-8 text-indigo-300 mx-auto mb-2" />
              <span className="text-white">Sports Teams</span>
            </Link>
            <Link to="/fantasy-football" className="bg-white/10 p-4 rounded-lg hover:bg-white/20 transition-colors">
              <Users className="w-8 h-8 text-indigo-300 mx-auto mb-2" />
              <span className="text-white">Fantasy Football</span>
            </Link>
            <Link to="/basketball" className="bg-white/10 p-4 rounded-lg hover:bg-white/20 transition-colors">
              <Brain className="w-8 h-8 text-indigo-300 mx-auto mb-2" />
              <span className="text-white">Basketball</span>
            </Link>
            <Link to="/superhero" className="bg-white/10 p-4 rounded-lg hover:bg-white/20 transition-colors">
              <Sparkles className="w-8 h-8 text-indigo-300 mx-auto mb-2" />
              <span className="text-white">Superhero Teams</span>
            </Link>
          </div>
        </div>
      </div>

      <section className="py-20 bg-indigo-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">
            The Most Advanced Team Name Generator
          </h2>
          <p className="text-gray-600 mb-12 max-w-2xl mx-auto">
            Our AI-powered platform combines advanced algorithms with deep understanding of team dynamics to create perfect names for any occasion.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
            <div>
              <Brain className="w-12 h-12 text-indigo-600 mx-auto mb-4" />
              <h3 className="text-xl font-semibold mb-2">AI-Powered</h3>
              <p className="text-gray-600">Advanced machine learning algorithms analyze thousands of successful team names</p>
            </div>
            <div>
              <Wand2 className="w-12 h-12 text-indigo-600 mx-auto mb-4" />
              <h3 className="text-xl font-semibold mb-2">Instant Results</h3>
              <p className="text-gray-600">Get creative, unique, and relevant team names in seconds</p>
            </div>
            <div>
              <Trophy className="w-12 h-12 text-indigo-600 mx-auto mb-4" />
              <h3 className="text-xl font-semibold mb-2">Category Specific</h3>
              <p className="text-gray-600">Specialized generators for sports, gaming, business, and more</p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div>
              <Target className="w-12 h-12 text-indigo-600 mx-auto mb-4" />
              <h3 className="text-xl font-semibold mb-2">Context Aware</h3>
              <p className="text-gray-600">Names that match your team's style, purpose, and values</p>
            </div>
            <div>
              <Sparkles className="w-12 h-12 text-indigo-600 mx-auto mb-4" />
              <h3 className="text-xl font-semibold mb-2">Always Fresh</h3>
              <p className="text-gray-600">Continuously updated with new trends and patterns</p>
            </div>
            <div>
              <Award className="w-12 h-12 text-indigo-600 mx-auto mb-4" />
              <h3 className="text-xl font-semibold mb-2">Free Forever</h3>
              <p className="text-gray-600">No cost, no limits - generate as many names as you need</p>
            </div>
          </div>
        </div>
      </section>
      
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">
            What Our Users Are Saying
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-gray-50 p-6 rounded-xl">
              <div className="flex items-center mb-4">
                <img
                  className="w-12 h-12 rounded-full mr-4"
                  src="https://images.unsplash.com/photo-1500648767791-00dcc994a43e?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=150&q=80"
                  alt="User avatar"
                />
                <div>
                  <h3 className="font-semibold">Michael S.</h3>
                  <p className="text-sm text-gray-500">Fantasy Football Manager</p>
                </div>
              </div>
              <p className="text-gray-600">"Found the perfect name for my fantasy football team in minutes. The AI suggestions were creative and hilarious!"</p>
            </div>
            <div className="bg-gray-50 p-6 rounded-xl">
              <div className="flex items-center mb-4">
                <img
                  className="w-12 h-12 rounded-full mr-4"
                  src="https://images.unsplash.com/photo-1438761681033-6461ffad8d80?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=150&q=80"
                  alt="User avatar"
                />
                <div>
                  <h3 className="font-semibold">Sarah L.</h3>
                  <p className="text-sm text-gray-500">Basketball Coach</p>
                </div>
              </div>
              <p className="text-gray-600">"As a coach, I needed a name that would inspire our youth team. The generator gave us the perfect choice!"</p>
            </div>
            <div className="bg-gray-50 p-6 rounded-xl">
              <div className="flex items-center mb-4">
                <img
                  className="w-12 h-12 rounded-full mr-4"
                  src="https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=150&q=80"
                  alt="User avatar"
                />
                <div>
                  <h3 className="font-semibold">David R.</h3>
                  <p className="text-sm text-gray-500">eSports Team Leader</p>
                </div>
              </div>
              <p className="text-gray-600">"The gaming-specific suggestions were exactly what we needed for our new eSports team. Highly recommended!"</p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-indigo-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Team Name Generation Made Easy
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Our platform makes it simple to find the perfect name for your team, no matter what category or style you're looking for.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="bg-white p-6 rounded-xl text-center">
              <div className="w-12 h-12 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-indigo-600">1</span>
              </div>
              <h3 className="font-semibold mb-2">Choose Category</h3>
              <p className="text-gray-600 text-sm">Select your team type from our specialized categories</p>
            </div>
            <div className="bg-white p-6 rounded-xl text-center">
              <div className="w-12 h-12 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-indigo-600">2</span>
              </div>
              <h3 className="font-semibold mb-2">Add Keywords</h3>
              <p className="text-gray-600 text-sm">Optionally include specific words or themes</p>
            </div>
            <div className="bg-white p-6 rounded-xl text-center">
              <div className="w-12 h-12 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-indigo-600">3</span>
              </div>
              <h3 className="font-semibold mb-2">Generate Names</h3>
              <p className="text-gray-600 text-sm">Get instant AI-powered suggestions</p>
            </div>
            <div className="bg-white p-6 rounded-xl text-center">
              <div className="w-12 h-12 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-indigo-600">4</span>
              </div>
              <h3 className="font-semibold mb-2">Choose Your Favorite</h3>
              <p className="text-gray-600 text-sm">Pick the perfect name for your team</p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">
            Start Generating Your Team Name
          </h2>
          <p className="text-center text-gray-600 mb-8 max-w-2xl mx-auto">
            Choose from our most popular categories and create the perfect team name in seconds.
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Link to="/sports" className="p-6 bg-indigo-50 rounded-lg hover:bg-indigo-100 transition-all transform hover:-translate-y-1 text-center group">
              <Trophy className="w-8 h-8 text-indigo-500 mx-auto mb-2 group-hover:scale-110 transition-transform" />
              Sports Teams
            </Link>
            <Link to="/fantasy-football" className="p-6 bg-indigo-50 rounded-lg hover:bg-indigo-100 transition-all transform hover:-translate-y-1 text-center group">
              <Users className="w-8 h-8 text-indigo-500 mx-auto mb-2 group-hover:scale-110 transition-transform" />
              Fantasy Football
            </Link>
            <Link to="/basketball" className="p-6 bg-indigo-50 rounded-lg hover:bg-indigo-100 transition-all transform hover:-translate-y-1 text-center group">
              <Brain className="w-8 h-8 text-indigo-500 mx-auto mb-2 group-hover:scale-110 transition-transform" />
              Basketball
            </Link>
            <Link to="/superhero" className="p-6 bg-indigo-50 rounded-lg hover:bg-indigo-100 transition-all transform hover:-translate-y-1 text-center group">
              <Sparkles className="w-8 h-8 text-indigo-500 mx-auto mb-2 group-hover:scale-110 transition-transform" />
              Superhero Teams
            </Link>
          </div>
          <p className="text-center text-gray-500 mt-8">
            Join over 50,000+ teams who found their perfect name with our generator
          </p>
        </div>
      </section>

      <section id="guide" className="py-24 bg-gray-50" itemScope itemType="https://schema.org/FAQPage">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" itemScope itemType="https://schema.org/Article">
          <article className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold text-center text-gray-900 mb-16" itemProp="headline">
              The Ultimate Guide to Team Name Generation
            </h2>
            
            <div className="prose prose-lg max-w-none mb-20">
              <div className="mb-16">
                <h3 className="text-3xl font-semibold text-gray-900 mb-6">
                  AI-Powered Team Name Generation: The Future of Team Branding
                </h3>
                <p className="text-gray-600 mb-6 leading-relaxed">
                  Team Name Genius leverages advanced artificial intelligence to create unique, memorable, and context-aware team names across multiple categories. Our sophisticated algorithms analyze successful team names, cultural references, and industry trends to generate names that resonate with your specific needs.
                </p>
                <div className="bg-indigo-50 p-6 rounded-xl">
                  <h4 className="text-xl font-semibold text-gray-900 mb-4">Key Features of Our Team Name Generator:</h4>
                  <ul className="grid grid-cols-1 md:grid-cols-2 gap-4 text-gray-600">
                    <li className="flex items-center gap-2">
                      <Star className="w-5 h-5 text-indigo-500" />
                      <span>Sport-specific algorithms</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <Star className="w-5 h-5 text-indigo-500" />
                      <span>Cultural relevance analysis</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <Star className="w-5 h-5 text-indigo-500" />
                      <span>Trend-based suggestions</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <Star className="w-5 h-5 text-indigo-500" />
                      <span>Contextual awareness</span>
                    </li>
                  </ul>
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 mb-20">
                <div>
                  <h3 className="text-2xl font-semibold text-gray-900 mb-6 flex items-center gap-3">
                    <Target className="w-6 h-6 text-indigo-500" />
                    Team Naming Best Practices
                  </h3>
                  <p className="text-gray-600 mb-6 leading-relaxed">
                    Creating the perfect team name involves understanding your audience, purpose, and brand identity. Our generator considers these factors while following established naming conventions and modern trends.
                  </p>
                  <ul className="list-disc list-inside text-gray-600 space-y-3">
                    <li>Audience-focused naming strategies</li>
                    <li>Brand identity integration</li>
                    <li>Modern naming conventions</li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-2xl font-semibold text-gray-900 mb-6 flex items-center gap-3">
                    <Zap className="w-6 h-6 text-indigo-500" />
                    Latest Naming Trends
                  </h3>
                  <p className="text-gray-600 mb-6 leading-relaxed">
                    Stay ahead with team names that reflect current trends while maintaining timeless appeal. Our AI continuously learns from successful team names across various categories.
                  </p>
                  <ul className="list-disc list-inside text-gray-600 space-y-3">
                    <li>Contemporary references</li>
                    <li>Cultural integration</li>
                    <li>Emerging patterns</li>
                  </ul>
                </div>
              </div>

              <div className="bg-indigo-50 p-12 rounded-2xl mb-20">
                <h3 className="text-2xl font-semibold text-gray-900 mb-8 flex items-center gap-3">
                  <Award className="w-6 h-6 text-indigo-500" />
                  What Makes a Great Team Name
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                  <div className="bg-white p-6 rounded-xl shadow-sm">
                    <h4 className="font-semibold text-gray-900 mb-3 text-xl">Memorability</h4>
                    <p className="text-gray-600">Easy to remember and distinctive names that stick with your audience</p>
                  </div>
                  <div className="bg-white p-6 rounded-xl shadow-sm">
                    <h4 className="font-semibold text-gray-900 mb-3 text-xl">Relevance</h4>
                    <p className="text-gray-600">Names that connect with your team's purpose and values</p>
                  </div>
                  <div className="bg-white p-6 rounded-xl shadow-sm">
                    <h4 className="font-semibold text-gray-900 mb-3 text-xl">Impact</h4>
                    <p className="text-gray-600">Names that make a lasting impression and build recognition</p>
                  </div>
                </div>
              </div>

              <div className="mb-20">
                <h3 className="text-2xl font-semibold text-gray-900 mb-6">
                  Category-Specific Naming Strategies
                </h3>
                <div className="space-y-6">
                  <div className="border-l-4 border-indigo-500 pl-6 py-2">
                    <h4 className="font-semibold text-gray-900 mb-3 text-xl">Sports Teams</h4>
                    <p className="text-gray-600">Focus on strength, unity, and local pride in sports team names.</p>
                  </div>
                  <div className="border-l-4 border-indigo-500 pl-6 py-2">
                    <h4 className="font-semibold text-gray-900 mb-3 text-xl">Fantasy Sports</h4>
                    <p className="text-gray-600">Combine humor, player references, and pop culture for engaging fantasy team names.</p>
                  </div>
                  <div className="border-l-4 border-indigo-500 pl-6 py-2">
                    <h4 className="font-semibold text-gray-900 mb-3 text-xl">Gaming & eSports</h4>
                    <p className="text-gray-600">Create names that reflect gaming culture and competitive spirit.</p>
                  </div>
                </div>
              </div>
              
              <div className="bg-white p-8 rounded-xl shadow-md mb-16">
                <h3 className="text-2xl font-semibold text-gray-900 mb-6">
                  Advanced Team Naming Techniques
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div>
                    <h4 className="font-semibold text-violet-800 mb-4">Linguistic Patterns:</h4>
                    <ul className="space-y-3 text-gray-700">
                      <li className="flex items-center gap-2">
                        <div className="w-2 h-2 bg-violet-500 rounded-full"></div>
                        <span>Alliteration (Thunder Titans)</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <div className="w-2 h-2 bg-violet-500 rounded-full"></div>
                        <span>Assonance (Dream Team)</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <div className="w-2 h-2 bg-violet-500 rounded-full"></div>
                        <span>Rhyming (Night Fight)</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <div className="w-2 h-2 bg-violet-500 rounded-full"></div>
                        <span>Compound Words (Stormbreakers)</span>
                      </li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-semibold text-violet-800 mb-4">Thematic Elements:</h4>
                    <ul className="space-y-3 text-gray-700">
                      <li className="flex items-center gap-2">
                        <div className="w-2 h-2 bg-violet-500 rounded-full"></div>
                        <span>Cultural References</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <div className="w-2 h-2 bg-violet-500 rounded-full"></div>
                        <span>Historical Significance</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <div className="w-2 h-2 bg-violet-500 rounded-full"></div>
                        <span>Geographic Elements</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <div className="w-2 h-2 bg-violet-500 rounded-full"></div>
                        <span>Industry Terminology</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
                <div className="bg-gradient-to-b from-indigo-50 to-white p-6 rounded-xl">
                  <h4 className="font-semibold text-gray-900 mb-4">Psychology of Names</h4>
                  <ul className="space-y-2 text-gray-600">
                    <li>• Emotional impact</li>
                    <li>• Memory retention</li>
                    <li>• Brand association</li>
                    <li>• Team motivation</li>
                  </ul>
                </div>
                <div className="bg-gradient-to-b from-indigo-50 to-white p-6 rounded-xl">
                  <h4 className="font-semibold text-gray-900 mb-4">Market Research</h4>
                  <ul className="space-y-2 text-gray-600">
                    <li>• Competitor analysis</li>
                    <li>• Trend monitoring</li>
                    <li>• Audience preferences</li>
                    <li>• Cultural sensitivity</li>
                  </ul>
                </div>
                <div className="bg-gradient-to-b from-indigo-50 to-white p-6 rounded-xl">
                  <h4 className="font-semibold text-gray-900 mb-4">Technical Aspects</h4>
                  <ul className="space-y-2 text-gray-600">
                    <li>• SEO optimization</li>
                    <li>• Social media impact</li>
                    <li>• Domain availability</li>
                    <li>• Trademark concerns</li>
                  </ul>
                </div>
              </div>

              <div className="bg-gradient-to-r from-indigo-50 to-white p-8 rounded-xl mb-16">
                <h3 className="text-2xl font-semibold text-gray-900 mb-6">
                  Name Generation Process
                </h3>
                <div className="space-y-6">
                  <div className="flex items-start gap-4">
                    <div className="w-8 h-8 bg-indigo-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-indigo-600 font-semibold">1</span>
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-2">Research & Analysis</h4>
                      <p className="text-gray-600">Study your target audience, competitors, and industry trends to understand what resonates.</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-8 h-8 bg-indigo-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-indigo-600 font-semibold">2</span>
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-2">Brainstorming</h4>
                      <p className="text-gray-600">Use AI-powered tools and creative techniques to generate initial name ideas.</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-8 h-8 bg-indigo-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-indigo-600 font-semibold">3</span>
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-2">Evaluation</h4>
                      <p className="text-gray-600">Assess each name against criteria like memorability, relevance, and uniqueness.</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-8 h-8 bg-indigo-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-indigo-600 font-semibold">4</span>
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-2">Validation</h4>
                      <p className="text-gray-600">Test names with your target audience and check for potential conflicts.</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white p-8 rounded-xl shadow-md">
                <h3 className="text-2xl font-semibold text-gray-900 mb-6">Expert Tips & Best Practices</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div>
                    <h4 className="font-semibold text-indigo-800 mb-4">Do's:</h4>
                    <ul className="space-y-3 text-gray-700">
                      <li className="flex items-center gap-2">
                        <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                        <span>Keep it memorable and unique</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                        <span>Consider your audience</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                        <span>Test for pronunciation</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                        <span>Future-proof your choice</span>
                      </li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-semibold text-indigo-800 mb-4">Don'ts:</h4>
                    <ul className="space-y-3 text-gray-700">
                      <li className="flex items-center gap-2">
                        <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                        <span>Use overused clichés</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                        <span>Make it too complex</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                        <span>Ignore trademark issues</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                        <span>Rush the decision</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
            
            <div itemScope itemType="https://schema.org/FAQPage">
              <h3 className="text-2xl font-semibold text-gray-900 mb-8">Frequently Asked Questions</h3>
              <div className="space-y-6">
                <div itemScope itemType="https://schema.org/Question" className="bg-white p-6 rounded-xl shadow-sm">
                  <h4 itemProp="name" className="font-semibold text-gray-900 mb-3">How does AI improve team name generation?</h4>
                  <div itemScope itemType="https://schema.org/Answer">
                    <p itemProp="text" className="text-gray-600">AI analyzes patterns in successful team names, considers cultural context, and generates unique combinations while maintaining relevance to your specific category and requirements.</p>
                  </div>
                </div>
                <div itemScope itemType="https://schema.org/Question" className="bg-white p-6 rounded-xl shadow-sm">
                  <h4 itemProp="name" className="font-semibold text-gray-900 mb-3">What makes a team name memorable?</h4>
                  <div itemScope itemType="https://schema.org/Answer">
                    <p itemProp="text" className="text-gray-600">Memorable team names often combine uniqueness, emotional resonance, and easy pronunciation. They may use literary devices like alliteration or incorporate relevant cultural references.</p>
                  </div>
                </div>
                <div itemScope itemType="https://schema.org/Question" className="bg-white p-6 rounded-xl shadow-sm">
                  <h4 itemProp="name" className="font-semibold text-gray-900 mb-3">How often should you update your team name?</h4>
                  <div itemScope itemType="https://schema.org/Answer">
                    <p itemProp="text" className="text-gray-600">Team names should be relatively permanent to build brand recognition. However, consider updates if your team's focus changes significantly or if the current name becomes outdated or problematic.</p>
                  </div>
                </div>
              </div>
            </div>
          </article>
        </div>
      </section>

      <section className="py-20 bg-indigo-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="flex justify-center mb-6">
            <Wand2 className="w-16 h-16 text-indigo-200" />
          </div>
          <h2 className="text-3xl font-bold text-white mb-6">
            Ready to Create Your Perfect Team Name?
          </h2>
          <p className="text-xl text-indigo-100 mb-8 max-w-3xl mx-auto">
            Join thousands of teams who've found their perfect name with our AI-powered generator.
            Start creating memorable, unique team names in seconds!
          </p>
          <div className="flex flex-wrap justify-center gap-4 mb-8">
            <span className="bg-white/20 px-4 py-2 rounded-full text-white text-sm">
              Instant Generation
            </span>
            <span className="bg-white/20 px-4 py-2 rounded-full text-white text-sm">
              AI-Powered
            </span>
            <span className="bg-white/20 px-4 py-2 rounded-full text-white text-sm">
              100% Free
            </span>
          </div>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link 
              to="/sports"
              onClick={(e) => {
                e.preventDefault();
                window.location.href = '/sports#sports-team-generator';
              }}
              className="bg-white text-indigo-600 text-lg px-8 py-4 rounded-lg hover:bg-indigo-50 transition-colors cursor-pointer shadow-lg hover:shadow-xl inline-flex items-center justify-center gap-2"
            >
              <Trophy className="w-5 h-5" />
              Get Started Now
            </Link>
            <button
              onClick={() => document.querySelector('#guide')?.scrollIntoView({ behavior: 'smooth' })}
              className="bg-indigo-500 text-white text-lg px-8 py-4 rounded-lg hover:bg-indigo-400 transition-colors cursor-pointer shadow-lg hover:shadow-xl inline-flex items-center justify-center gap-2 border border-indigo-400"
            >
              <Info className="w-5 h-5" />
              Learn More
            </button>
          </div>
          <p className="mt-8 text-sm text-indigo-200">
            No credit card required • Instant access • Start generating names now
          </p>
        </div>
      </section>
    </>
  );
}